import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import { useMemo } from 'react';
import { formatRoas } from '@/components/analytics/CampaignsTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatCount, formatCurrency, presetToRange } from '@/lib/analytics';
import type { ApiClient } from '@/lib/analytics-types';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';

/**
 * Dashboard home / overview. Shows the active workspace, headline counts
 * (domains / landers / leads) pulled from the existing list endpoints, the
 * last-7-days money metrics (spend / revenue / ROAS / calls / conversions from
 * GET /api/analytics/overview — FF-12), a per-domain rollup table whose rows
 * deep-link into the FF-22 funnel overview, and quick links into each section.
 * Queries are keyed by the active tenant so they refetch when the workspace
 * switches (useApi re-injects X-Tenant-Id). Cents→dollars at the render edge
 * only; null ROAS renders '—' (zero states for empty tenants, never NaN).
 */
export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
});

// Overview response inferred from the typed RPC client (FF-12 contract).
// `InferResponseType` is the UNION of every status body; narrow to the success
// member by the discriminating `byDomain` key (same pattern as analytics/index).
type OverviewResponse = InferResponseType<ApiClient['api']['analytics']['overview']['$get']>;
type OverviewSuccess = Extract<OverviewResponse, { byDomain: unknown }>;
type OverviewDomainRow = OverviewSuccess['byDomain'][number];

function StatCard({
  label,
  value,
  to,
}: {
  label: string;
  value: number | string | undefined;
  to: string;
}): React.ReactElement {
  return (
    <Link to={to} className="block transition-shadow hover:shadow-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-gray-500">{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <span className="text-3xl font-semibold text-gray-900">{value ?? '—'}</span>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Per-domain rollup (host, visits, leads, calls, revenue, spend, ROAS) for the
 * default last-7-days window. Row-click (or Enter) navigates to the domain's
 * funnel overview (/domains/$domainId/analytics — FF-22). Inline table rather
 * than DataTable because the rows themselves are the navigation affordance.
 */
function DomainRollupTable({ rows }: { rows: OverviewDomainRow[] }): React.ReactElement {
  const navigate = useNavigate();
  const open = (domainId: number): void => {
    void navigate({
      to: '/domains/$domainId/analytics',
      params: { domainId: String(domainId) },
    });
  };
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Domain</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Visits</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Leads</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Calls</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Revenue</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Spend</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">ROAS</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                No domain activity in this window yet.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.domainId}
                tabIndex={0}
                className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                onClick={() => open(row.domainId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') open(row.domainId);
                }}
              >
                <td className="px-4 py-3 font-medium text-gray-900">{row.host}</td>
                <td className="px-4 py-3 tabular-nums text-gray-900">{formatCount(row.visits)}</td>
                <td className="px-4 py-3 tabular-nums text-gray-900">{formatCount(row.leads)}</td>
                <td className="px-4 py-3 tabular-nums text-gray-900">{formatCount(row.calls)}</td>
                <td className="px-4 py-3 tabular-nums text-gray-900">
                  {formatCurrency(row.revenueCents)}
                </td>
                <td className="px-4 py-3 tabular-nums text-gray-900">
                  <span
                    className="underline decoration-dotted underline-offset-2"
                    title={`FB costs ${formatCurrency(row.spendFbCostsCents)} + creative ${formatCurrency(row.spendCreativeCents)}`}
                  >
                    {formatCurrency(row.spendCents)}
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums text-gray-900">{formatRoas(row.roas)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const SECTIONS: { label: string; description: string; to: string }[] = [
  {
    label: 'Domains',
    description: 'Add custom hostnames and manage provisioning.',
    to: '/domains',
  },
  { label: 'Landers', description: 'Create and edit landing-page variants.', to: '/landers' },
  { label: 'Leads', description: 'Browse submitted form leads.', to: '/leads' },
  {
    label: 'Analytics',
    description: 'Funnel, attribution, and conversion metrics.',
    to: '/analytics',
  },
  { label: 'Team', description: 'Manage members and per-domain access.', to: '/settings/team' },
  { label: 'Settings', description: 'Your profile and account.', to: '/settings/profile' },
];

function DashboardPage(): React.ReactElement {
  const api = useApi();
  const { activeTenantId } = useTenantContext();
  const ready = Boolean(activeTenantId);

  // Active workspace name (from /api/me — the same source the switcher uses).
  const { data: tenants } = useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: async () => {
      const res = await api.api.me.$get();
      if (!res.ok) throw new Error('Failed to load workspace');
      const data = await res.json();
      return data.tenants;
    },
  });
  const active = tenants?.find((t) => t.id === activeTenantId);
  const workspaceName = active?.brandName || active?.id || activeTenantId || 'Workspace';

  const { data: domainCount } = useQuery({
    queryKey: ['domains', 'count', activeTenantId],
    enabled: ready,
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) throw new Error('domains');
      return (await res.json()).domains.length;
    },
  });
  const { data: landerCount } = useQuery({
    queryKey: ['landers', 'count', activeTenantId],
    enabled: ready,
    queryFn: async () => {
      const res = await api.api.landers.$get();
      if (!res.ok) throw new Error('landers');
      return (await res.json()).landers.length;
    },
  });
  const { data: leadCount } = useQuery({
    queryKey: ['leads', 'count', activeTenantId],
    enabled: ready,
    queryFn: async () => {
      const res = await api.api.leads.$get();
      if (!res.ok) throw new Error('leads');
      return (await res.json()).leads.length;
    },
  });

  // FF-29: tenant-wide money metrics over the default last-7-days ET window.
  // The window is resolved once per mount so the query key stays stable.
  const range = useMemo(() => presetToRange('7d'), []);
  const { data: overview } = useQuery({
    queryKey: ['dashboard-overview', activeTenantId, range],
    enabled: ready,
    queryFn: async () => {
      const res = await api.api.analytics.overview.$get({
        query: range ? { from: range.from, to: range.to } : {},
      });
      if (!res.ok) throw new Error('overview');
      return await res.json();
    },
  });
  const totals = overview?.totals;

  return (
    <main className="mx-auto max-w-6xl p-8">
      <header className="mb-8">
        <p className="text-sm font-medium text-gray-500">Workspace</p>
        <h1 className="text-2xl font-semibold text-gray-900">{workspaceName}</h1>
      </header>

      <section className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Domains" value={domainCount} to="/domains" />
        <StatCard label="Landers" value={landerCount} to="/landers" />
        <StatCard label="Leads" value={leadCount} to="/leads" />
      </section>

      <h2 className="mb-1 text-lg font-semibold text-gray-900">Last 7 days</h2>
      <p className="mb-4 text-sm text-gray-500">
        Spend, revenue, and call volume across the workspace (ET days).
      </p>
      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Spend"
          value={totals ? formatCurrency(totals.spendCents) : undefined}
          to="/analytics"
        />
        <StatCard
          label="Revenue"
          value={totals ? formatCurrency(totals.revenueCents) : undefined}
          to="/analytics"
        />
        <StatCard
          label="ROAS"
          value={totals ? formatRoas(totals.roas) : undefined}
          to="/analytics"
        />
        <StatCard
          label="Calls"
          value={totals ? formatCount(totals.calls) : undefined}
          to="/analytics"
        />
        <StatCard
          label="Conversions"
          value={totals ? formatCount(totals.conversions) : undefined}
          to="/analytics"
        />
      </section>

      <section className="mb-10">
        <DomainRollupTable rows={overview?.byDomain ?? []} />
      </section>

      <h2 className="mb-4 text-lg font-semibold text-gray-900">Manage</h2>
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SECTIONS.map((s) => (
          <Link key={s.to} to={s.to} className="block transition-shadow hover:shadow-md">
            <Card>
              <CardHeader>
                <CardTitle>{s.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">{s.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </section>
    </main>
  );
}
